import { useMemo } from "react";
import { Zap, Infinity as InfinityIcon, Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * UsageMeter — shows monthly enrichment usage vs. tier limit (+ purchased credits).
 * Hidden if user/usage isn't loaded yet.
 */
export default function UsageMeter({ onUpgrade, onBuyCredits }) {
  const { user } = useAuth();
  const usage = user?.usage;

  const computed = useMemo(() => {
    if (!usage) return null;
    const {
      used = 0,
      limit,
      effective_limit,
      purchased_credits = 0,
      overage_count = 0,
      overage_cost = 0,
      hard_cap,
    } = usage;
    if (limit == null) {
      return {
        used,
        limit: null,
        effective_limit: null,
        purchased_credits: 0,
        pct: 0,
        urgent: false,
        exceeded: false,
        overage_count: 0,
        overage_cost: 0,
      };
    }
    const eff = effective_limit ?? limit;
    const pct = Math.min(100, Math.round((used / eff) * 100));
    return {
      used,
      limit,
      effective_limit: eff,
      purchased_credits,
      pct,
      urgent: pct >= 80 && pct < 100,
      exceeded: used >= eff,
      overage_count,
      overage_cost,
      hard_cap,
    };
  }, [usage]);

  if (!user || !computed) return null;

  const tier = (user.tier || "free").toLowerCase();
  const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
  const {
    used,
    limit,
    effective_limit,
    purchased_credits,
    pct,
    urgent,
    exceeded,
    overage_count,
    overage_cost,
  } = computed;

  // bar color
  const barColor = exceeded
    ? "bg-[#C62828]"
    : urgent
    ? "bg-[#F57F17]"
    : "bg-ink-900";

  const isUnlimited = limit == null;

  // Compact inline chip for unlimited tiers — no big card, no progress bar
  if (isUnlimited) {
    return (
      <div
        data-testid="usage-meter"
        className="inline-flex items-center gap-2 mb-6 text-[11px] font-mono text-ink-500"
      >
        <Zap className="w-3.5 h-3.5 text-ink-400" />
        <span className="uppercase tracking-widestx text-[10px] text-ink-400">
          {tierLabel} plan
        </span>
        <span className="text-ink-300">·</span>
        <span
          data-testid="usage-meter-text"
          className="font-semibold text-ink-900 inline-flex items-center gap-1"
        >
          {used.toLocaleString()}
          <span className="text-ink-300">/</span>
          <InfinityIcon className="w-3.5 h-3.5 text-ink-700" />
          <span className="text-ink-400 font-normal ml-1">unlimited enrichments</span>
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="usage-meter"
      className="bg-white border border-ink-100 rounded-sm p-4 mb-6"
    >
      <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <Zap className="w-4 h-4 text-ink-400" />
          <div>
            <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
              Monthly enrichments · {tierLabel} plan
            </div>
            <div
              data-testid="usage-meter-text"
              className="text-sm font-semibold text-ink-900 mt-0.5 font-mono"
            >
              {used.toLocaleString()}
              <span className="text-ink-300"> / </span>
              {effective_limit.toLocaleString()}
              {purchased_credits > 0 && (
                <span className="ml-2 text-[10px] uppercase tracking-widestx font-semibold text-[#2E7D32]">
                  +{purchased_credits.toLocaleString()} bought
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {(urgent || exceeded) && onBuyCredits && (
            <button
              data-testid="usage-meter-buy-credits"
              onClick={onBuyCredits}
              className="text-[10px] uppercase tracking-widestx font-semibold px-3 py-1.5 rounded-sm bg-ink-900 hover:bg-ink-700 text-white inline-flex items-center gap-1"
            >
              <Plus className="w-3 h-3" />
              Buy credits
            </button>
          )}
          {(urgent || exceeded) && (
            <button
              data-testid="usage-meter-upgrade"
              onClick={onUpgrade}
              className={`text-[10px] uppercase tracking-widestx font-semibold px-3 py-1.5 rounded-sm transition-colors ${
                exceeded
                  ? "bg-[#C62828] hover:bg-[#A11414] text-white"
                  : "bg-[#F57F17] hover:bg-[#E65100] text-white"
              }`}
            >
              {exceeded ? "Upgrade now" : "Upgrade"}
            </button>
          )}
        </div>
      </div>

      <div className="relative w-full h-2 bg-ink-100 rounded-sm overflow-hidden">
        <div
          data-testid="usage-meter-bar"
          className={`absolute left-0 top-0 h-full ${barColor} transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {exceeded && overage_count === 0 && (
        <p
          data-testid="usage-meter-exceeded-msg"
          className="text-[11px] text-[#C62828] font-medium mt-2"
        >
          You've hit your monthly cap. Buy credits or upgrade to keep going.
        </p>
      )}

      {overage_count > 0 && (
        <div
          data-testid="usage-meter-overage"
          className="mt-3 pt-3 border-t border-ink-100 flex items-center justify-between gap-3 flex-wrap"
        >
          <div>
            <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
              Overage this month
            </div>
            <div className="text-sm font-semibold text-[#C62828] mt-0.5 font-mono">
              {overage_count.toLocaleString()} leads · $
              {Number(overage_cost).toFixed(2)} owed
            </div>
            <div className="text-[10px] text-ink-500 mt-0.5">
              Billed at $0.15 / lead. Buy a credit pack to clear it now.
            </div>
          </div>
          {onBuyCredits && (
            <button
              data-testid="usage-meter-overage-buy"
              onClick={onBuyCredits}
              className="text-[10px] uppercase tracking-widestx font-semibold px-3 py-1.5 rounded-sm bg-ink-900 hover:bg-ink-700 text-white"
            >
              Buy credits
            </button>
          )}
        </div>
      )}
    </div>
  );
}
